import type { CreateToastFnReturn } from '@chakra-ui/react';
import mixpanel from 'mixpanel-browser';
import PptxGenJS from 'pptxgenjs';

import { type ChartType, FeatureTrackingEvents } from '@revelio/core';
import type { UploadFileResult } from '@revelio/data-access';
import {
  NEGATIVE_COLOR,
  POSITIVE_COLOR,
  ReplotChartNames,
} from '@revelio/replots';

import { DataType } from '../../../../DataProvider/types';
import { MIXED_STATS_DATA_TYPE } from '../registry';
import {
  buildSheetData,
  generateChartData,
  processMixedStatsData,
} from './ppt-data-processor';
import {
  addDivergingBarComboSlide,
  addFooterSlide,
  addMixedStatsSlide,
  addSlideContent,
  addTableSlide,
  addTextSlide,
} from './ppt-slide-generator';
import { defineSlideMasters, PptxChartType } from './ppt-theme';

export type PptReplotChartType = ReplotChartNames;

type ChartExportParams = {
  title: string;
  defaultFileName?: string;
  chartType?: PptReplotChartType;
  plotChartType?: ChartType;
  selectedSegments?: { name: string }[];
  data?: unknown;
  dataType?: string;
  textContent?: string;
};

type PptExportResult = {
  getFile: () => Promise<void>;
  getBlob: () => Promise<{ blob: Blob; fileName: string } | null>;
};

export const mapReplotToPptChart = (
  replotChartType?: PptReplotChartType | null
) => {
  switch (replotChartType) {
    case ReplotChartNames.LineChart:
      return {
        type: PptxChartType.line,
        opts: {
          lineGrouping: 'percentStacked',
          lineDataSymbol: 'none',
          lineSize: 1,
          showLegend: true,
          legendPos: 'r',
          catLabelFormatCode: 'yyyy-mm',
          catAxisLabelFrequency: '10',
        },
      };
    case ReplotChartNames.BarChart:
      return {
        type: PptxChartType.bar,
        opts: {
          barDir: 'bar',
        },
      };
    case ReplotChartNames.StackedBarChartHorizontal:
      return {
        type: PptxChartType.bar,
        opts: {
          barGrouping: 'percentStacked',
          barDir: 'bar',
          showLegend: true,
          legendPos: 'b',
        },
      };
    case ReplotChartNames.GroupedBarChartHorizontal:
      return {
        type: PptxChartType.bar,
        opts: {
          barDir: 'bar',
          showLegend: true,
          legendPos: 'b',
          barGapWidthPct: 25,
        },
      };
    case ReplotChartNames.DivergingBarChart:
      return {
        type: PptxChartType.bar,
        opts: {
          barDir: 'bar',
          barGrouping: 'stacked',
          showLegend: false,
          axisLabelFormatCode: '#%',
          catAxisLabelPos: 'low',
          valueBarColors: true,
          chartColors: [
            POSITIVE_COLOR.replace('#', ''),
            NEGATIVE_COLOR.replace('#', ''),
          ],
          catAxisLabelFontSize: 10,
        },
      };
    case ReplotChartNames.BoxPlot:
      return {
        type: PptxChartType.bar,
        opts: {
          barGrouping: 'clustered',
          barDir: 'bar',
          showLegend: true,
          legendPos: 'b',
          catAxisMultiLevelLabels: true,
          catAxisLabelFontFace: 'Arial',
          catAxisLabelFontSize: 10,
        },
      };
    case ReplotChartNames.KDE:
      return {
        type: PptxChartType.area,
        opts: {
          showLegend: true,
          legendPos: 'b',
          chartColorsOpacity: 60,
        },
      };
    case ReplotChartNames.VolumeChart:
      return {
        type: 'combo',
        opts: {
          showLegend: true,
          legendPos: 'b',
        },
      };
    case ReplotChartNames.DivergingBarCombo:
      return {
        type: 'combo-diverging',
        opts: {},
      };
    case ReplotChartNames.TableChart:
      // Tables are rendered via addTableSlide before this mapping is reached;
      // return a no-op placeholder so the type stays explicit here.
      return null;
    // biome-ignore lint/complexity/noUselessSwitchCase: migrated from develop
    case ReplotChartNames.Sankey:
    default:
      return {
        type: PptxChartType.bar,
        opts: {
          showLegend: true,
          legendPos: 'b',
        },
      };
  }
};

const createSinglePptxDoc = async (
  params: ChartExportParams
): Promise<{
  pptx: PptxGenJS;
  baseName: string;
  fileName: string;
  slideAdded: boolean;
}> => {
  const {
    title,
    defaultFileName,
    chartType,
    plotChartType,
    selectedSegments,
    data,
    dataType,
    textContent,
  } = params;

  const pptx = new PptxGenJS();
  defineSlideMasters(pptx);

  const baseName = defaultFileName
    ? defaultFileName.split(' - ').slice(0, -1).join(' - ')
    : title;
  const fileName = `${baseName}.pptx`;

  const titleSlide = pptx.addSlide({ masterName: 'TITLE_SLIDE' });
  titleSlide.addText(baseName, { placeholder: 'report_name' });

  const slideAdded = await addChartSlide(pptx, {
    title,
    defaultFileName,
    chartType,
    plotChartType,
    selectedSegments,
    data,
    dataType,
    textContent,
  });

  addFooterSlide(pptx);

  return { pptx, baseName, fileName, slideAdded };
};

export const exportSingleChartToPpt = (
  params: ChartExportParams
): PptExportResult => ({
  async getFile() {
    const { fileName, slideAdded, pptx } = await createSinglePptxDoc(params);

    mixpanel.track(FeatureTrackingEvents.PPT_DOWNLOAD, {
      plot_title: params.title,
    });

    if (!slideAdded) return;
    await pptx.writeFile({ fileName });
  },
  async getBlob() {
    const { fileName, slideAdded, pptx } = await createSinglePptxDoc(params);
    if (!slideAdded) return null;
    const blob = (await pptx.write({ outputType: 'blob' })) as Blob;

    mixpanel.track(FeatureTrackingEvents.PPT_PREVIEW, {
      plot_title: params.title,
    });

    return { blob, fileName };
  },
});

const addChartSlide = async (
  pptx: PptxGenJS,
  chartParams: ChartExportParams
): Promise<boolean> => {
  const { title, defaultFileName, selectedSegments, data, dataType } =
    chartParams;

  if (chartParams.dataType === 'text') {
    const textBody = chartParams.textContent?.trim();
    if (textBody) {
      await addTextSlide(pptx, title, textBody, defaultFileName);
      return true;
    }
    return false;
  }

  const isDivergingBarComboExport =
    chartParams.plotChartType === 'diverging-bar-combo' ||
    chartParams.chartType === ReplotChartNames.DivergingBarCombo;

  if (isDivergingBarComboExport) {
    if (!data) {
      console.warn(`No data to export for slide: ${title}`);
      return false;
    }
    const dataSlide = pptx.addSlide();
    addDivergingBarComboSlide(
      dataSlide,
      title,
      defaultFileName,
      data,
      dataType,
      selectedSegments
    );
    return true;
  }

  const replotChartType = getReplotChartType(chartParams);
  const { sheetData } = buildSheetData(data, dataType, replotChartType);
  if (!sheetData || sheetData.length === 0) {
    console.warn(`No data to export for slide: ${title}`);
    return false;
  }

  if (replotChartType === ReplotChartNames.TableChart) {
    addTableSlide(
      pptx,
      sheetData as Record<string, string | number>[],
      title,
      defaultFileName
    );
    return true;
  }

  const pptChart = mapReplotToPptChart(replotChartType);

  if (dataType === MIXED_STATS_DATA_TYPE) {
    const charts = processMixedStatsData(sheetData) || [];
    addMixedStatsSlide(pptx, charts, title, defaultFileName);
  } else {
    const dataSlide = pptx.addSlide();
    const chartData = generateChartData(sheetData, replotChartType);

    addSlideContent(
      dataSlide,
      title,
      defaultFileName,
      chartData,
      pptChart as unknown as {
        type: PptxGenJS.CHART_NAME;
        opts?: Record<string, unknown>;
      },
      replotChartType ?? undefined,
      selectedSegments
    );
  }
  return true;
};

const createMultiplePptxDoc = async (
  charts: ChartExportParams[]
): Promise<{ pptx: PptxGenJS; baseTitle: string; fileName: string }> => {
  const pptx = new PptxGenJS();
  defineSlideMasters(pptx);

  const lastDefaultFileName = charts[charts.length - 1]?.defaultFileName;
  const baseTitle = lastDefaultFileName
    ? lastDefaultFileName.split(' - ').slice(0, -1).join(' - ')
    : 'dashboard-export';

  const titleSlide = pptx.addSlide({ masterName: 'TITLE_SLIDE' });
  titleSlide.addText(baseTitle || '', { placeholder: 'report_name' });

  for (const c of charts) {
    await addChartSlide(pptx, c);
  }

  addFooterSlide(pptx);

  const finalName = `${baseTitle}.pptx`;
  return { pptx, baseTitle, fileName: finalName };
};

export const exportMultipleChartsToPpt = ({
  charts,
}: {
  charts: ChartExportParams[];
}): PptExportResult => ({
  async getFile() {
    const { fileName, pptx } = await createMultiplePptxDoc(charts);

    mixpanel.track(FeatureTrackingEvents.PPT_DOWNLOAD, {
      plot_title: charts[charts.length - 1]?.title,
    });

    await pptx.writeFile({ fileName });
  },
  async getBlob() {
    if (!charts.length) return null;
    const { fileName, pptx } = await createMultiplePptxDoc(charts);
    const blob = (await pptx.write({ outputType: 'blob' })) as Blob;

    mixpanel.track(FeatureTrackingEvents.PPT_PREVIEW, {
      plot_title: charts[charts.length - 1]?.title,
    });

    return { blob, fileName };
  },
});

const mapPlotChartTypeToReplotChartType = (
  plotChartType: ChartType
): PptReplotChartType | null => {
  switch (plotChartType) {
    case 'bar':
      return ReplotChartNames.BarChart;
    case 'line':
      return ReplotChartNames.LineChart;
    case 'stacked-bar':
      return ReplotChartNames.StackedBarChartHorizontal;
    case 'grouped-bar':
      return ReplotChartNames.GroupedBarChartHorizontal;
    case 'diverging-bar':
      return ReplotChartNames.DivergingBarChart;
    case 'diverging-bar-combo':
      return ReplotChartNames.DivergingBarCombo;
    case 'distribution':
      return ReplotChartNames.KDE;
    case 'volume':
      return ReplotChartNames.VolumeChart;
    case 'box':
      return ReplotChartNames.BoxPlot;
    case 'table':
      return ReplotChartNames.TableChart;
    default:
      return null;
  }
};

export const getReplotChartType = (
  chartParams: ChartExportParams
): PptReplotChartType | null => {
  const { chartType, plotChartType, dataType } = chartParams;
  if (chartType !== undefined) {
    return chartType;
  }

  if (plotChartType) {
    const mapped = mapPlotChartTypeToReplotChartType(plotChartType);
    if (mapped) return mapped;
  }

  if (dataType === DataType.DISTRIBUTION) {
    return ReplotChartNames.KDE;
  }

  console.warn('Unhandled data type for PPT export: ', dataType);

  return null;
};

export const showExportToast = (
  toast: CreateToastFnReturn,
  promise: Promise<void>
) =>
  toast.promise(promise, {
    loading: {
      title: 'Exporting file...',
      position: 'top-right',
    },
    success: {
      title: 'File exported',
      position: 'top-right',
      duration: 1500,
    },
    error: (error) => ({
      title: 'File export failed',
      description:
        (error && (error as Error).message) || 'Something went wrong.',
      position: 'top-right',
    }),
  });

export const showUploadToast = (
  toast: CreateToastFnReturn,
  promise: Promise<UploadFileResult | undefined>
) =>
  toast.promise(promise, {
    loading: {
      title: 'Preparing your slides...',
      position: 'top-right',
    },
    success: {
      title: 'Slides are ready',
      position: 'top-right',
      duration: 1500,
    },
    error: (error) => ({
      title: 'Slides generation failed',
      description:
        (error && (error as Error).message) || 'Something went wrong.',
      position: 'top-right',
    }),
  });
